import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { accentBg } from './badge-colors'

/**
 * Badge — small label for status, categories, counts, or metadata. Design adopted
 * from the TailGrids badge model (the system our Figma is built on), wired to
 * First Bite tokens:
 *
 *   size   sm | md | lg                       (height + padding + text size)
 *   color  semantic: gray | primary | error | warning | success
 *          categorical: cyan | sky | blue | indigo | violet | purple | fuchsia |
 *          pink | rose | orange | amber | yellow | lime | emerald | teal
 *          (categorical set = the assignable deal-stage palette)
 *   dot         boolean        leading status dot (uses the color's accent shade)
 *   prefixIcon  ReactNode      icon before the text (inherits text color, 12px)
 *   suffixIcon  ReactNode      icon after the text  (inherits text color, 12px)
 *   onDismiss   () => void     renders a trailing × button
 *
 * Every color resolves to `fbui-*` tokens (bg-fbui-cyan-50, text-fbui-cyan-700,
 * …) so badges track the design-system palette and stay dark-mode ready. Each
 * color is a soft -50 surface (gray uses -100) with -700 text; the dot uses the
 * brighter -500/-600 accent to match the Figma "Icon" color.
 */
export const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full font-medium tracking-[-0.2px] [&>svg]:size-3 [&>svg]:shrink-0',
  {
    variants: {
      size: {
        sm: 'gap-1 px-2 py-0.5 text-xs',
        md: 'gap-1 px-2.5 py-0.5 text-sm',
        lg: 'gap-1.5 px-3 py-1 text-sm',
      },
      color: {
        gray: 'bg-fbui-gray-100 text-fbui-gray-700',
        primary: 'bg-fbui-basil-50 text-fbui-basil-700',
        error: 'bg-fbui-red-50 text-fbui-red-700',
        warning: 'bg-fbui-amber-50 text-fbui-amber-700',
        success: 'bg-fbui-green-50 text-fbui-green-700',
        cyan: 'bg-fbui-cyan-50 text-fbui-cyan-700',
        sky: 'bg-fbui-sky-50 text-fbui-sky-700',
        blue: 'bg-fbui-blue-50 text-fbui-blue-700',
        indigo: 'bg-fbui-indigo-50 text-fbui-indigo-700',
        violet: 'bg-fbui-violet-50 text-fbui-violet-700',
        purple: 'bg-fbui-purple-50 text-fbui-purple-700',
        fuchsia: 'bg-fbui-fuchsia-50 text-fbui-fuchsia-700',
        pink: 'bg-fbui-pink-50 text-fbui-pink-700',
        rose: 'bg-fbui-rose-50 text-fbui-rose-700',
        orange: 'bg-fbui-orange-50 text-fbui-orange-700',
        amber: 'bg-fbui-amber-50 text-fbui-amber-700',
        yellow: 'bg-fbui-yellow-50 text-fbui-yellow-700',
        lime: 'bg-fbui-lime-50 text-fbui-lime-700',
        emerald: 'bg-fbui-emerald-50 text-fbui-emerald-700',
        teal: 'bg-fbui-teal-50 text-fbui-teal-700',
      },
    },
    defaultVariants: { size: 'sm', color: 'primary' },
  }
)

export type BadgeColor = NonNullable<VariantProps<typeof badgeVariants>['color']>

/**
 * The full badge palette, ordered semantic-first then categorical. Single source
 * for Storybook color controls so the list can't drift across stories.
 */
export const BADGE_COLORS = [
  'gray',
  'primary',
  'error',
  'warning',
  'success',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
  'orange',
  'amber',
  'yellow',
  'lime',
  'emerald',
  'teal',
] as const satisfies readonly BadgeColor[]

export interface BadgeProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    VariantProps<typeof badgeVariants> {
  /** Render a leading status dot in the color's accent shade. */
  dot?: boolean
  /** Icon rendered before the text (sized to 12px, inherits text color). */
  prefixIcon?: React.ReactNode
  /** Icon rendered after the text (sized to 12px, inherits text color). */
  suffixIcon?: React.ReactNode
  /** When provided, renders a trailing × button that calls this on click. */
  onDismiss?: () => void
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      size,
      color,
      dot,
      prefixIcon,
      suffixIcon,
      onDismiss,
      children,
      ...props
    },
    ref
  ) => {
    const resolvedColor: BadgeColor = color ?? 'primary'
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ size, color, className }))}
        {...props}
      >
        {dot && (
          <span
            aria-hidden="true"
            className={cn('size-2 shrink-0 rounded-full', accentBg[resolvedColor])}
          />
        )}
        {prefixIcon}
        {children}
        {suffixIcon}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="-mr-0.5 inline-flex shrink-0 items-center justify-center rounded-full opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-fbui-ring-default"
          >
            <X weight="bold" className="size-3" aria-hidden="true" />
          </button>
        )}
      </span>
    )
  }
)
Badge.displayName = 'Badge'
